import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import { Login as JoinIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface AdminDialogProps {
  open: boolean;
  onClose: () => void;
  socketRef?: React.MutableRefObject<WebSocket | null>;
  sendMessage?: (message: any) => void;
  onGpsTestingChange?: (active: boolean) => void;
  onGpsTestClick?: (lat: number, lng: number) => void;
}

export function AdminDialog({ open, onClose, socketRef, sendMessage, onGpsTestingChange, onGpsTestClick }: AdminDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [appInfo, setAppInfo] = useState<any>(null);
  const [rooms, setRooms] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [gpsTestingActive, setGpsTestingActive] = useState(false);

  useEffect(() => {
    if (open && socketRef?.current) {
      setLoading(true);
      // Query application info
      socketRef.current.send(JSON.stringify({
        type: 'query_app_info'
      }));
      
      // Query all rooms
      socketRef.current.send(JSON.stringify({
        type: 'query_all_rooms'
      }));

      // Set up listener for responses
      const handleMessage = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'app_info_response') {
            setAppInfo(message.data);
            setLoading(false);
          } else if (message.type === 'all_rooms_response') {
            setRooms(message.rooms || []);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error parsing admin response:', error);
        }
      };

      socketRef.current.addEventListener('message', handleMessage);

      // Cleanup
      return () => {
        if (socketRef.current) {
          socketRef.current.removeEventListener('message', handleMessage);
        }
      };
    }
  }, [open, socketRef]);

  const handleJoinRoom = (roomId: string) => {
    // Join the room by redirecting with the room parameter
    const url = new URL(window.location.href);
    url.searchParams.set('room', roomId);
    window.location.href = url.toString();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('Admin Information')}</DialogTitle>
      <DialogContent>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label={t('Statistics')} />
          <Tab label={t('Rooms')} />
          <Tab label={t('GPS Testing')} />
        </Tabs>
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Statistics Tab */}
            {tabValue === 0 && appInfo && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('Application Statistics')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Box mb={2}>
                  <Typography variant="subtitle1" color="primary">
                    {t('Active Users')}
                  </Typography>
                  <Typography variant="body1">
                    {appInfo.activeUsers || 0} {t('users online')}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle1" color="primary">
                    {t('Active Rooms')}
                  </Typography>
                  <Typography variant="body1">
                    {appInfo.activeRooms || 0} {t('rooms in use')}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle1" color="primary">
                    {t('Total Geometries')}
                  </Typography>
                  <Typography variant="body1">
                    {appInfo.totalGeometries || 0} {t('geometries created')}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle1" color="primary">
                    {t('Server Uptime')}
                  </Typography>
                  <Typography variant="body1">
                    {appInfo.uptime || 'N/A'}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle1" color="primary">
                    {t('Memory Usage')}
                  </Typography>
                  <Typography variant="body1">
                    {appInfo.memoryUsage ? `${Math.round(appInfo.memoryUsage / 1024 / 1024)} MB` : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Rooms Tab */}
            {tabValue === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('All Rooms')} ({rooms.length})
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                {rooms.length === 0 ? (
                  <Typography variant="body1" color="textSecondary">
                    {t('No rooms available')}
                  </Typography>
                ) : (
                  <List>
                    {rooms.map((room) => (
                      <Paper key={room.id} sx={{ mb: 1 }}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>
                                  {room.name || room.id}
                                </span>
                                {room.isCurrentRoom && (
                                  <Chip label={t('Current')} color="primary" size="small" />
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                <span style={{ display: 'block' }}>
                                  ID: {room.id}
                                </span>
                                <span style={{ display: 'block' }}>
                                  {t('Users')}: {room.activeUsers}/{room.totalUsers} {t('active')}
                                </span>
                                <span style={{ display: 'block' }}>
                                  {t('Geometries')}: {room.geometryCount || 0}
                                </span>
                                <span style={{ display: 'block' }}>
                                  {t('Canopy Prediction')}: {room.hasCanopyPrediction ? t('Yes') : t('No')}
                                </span>
                                <span style={{ display: 'block' }}>
                                  {t('Created')}: {new Date(room.createdAt).toLocaleString()}
                                </span>
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="join"
                              onClick={() => handleJoinRoom(room.id)}
                              disabled={room.isCurrentRoom}
                              color="primary"
                            >
                              <JoinIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </Paper>
                    ))}
                  </List>
                )}
              </Box>
            )}
            
            {/* GPS Testing Tab */}
            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('GPS Testing')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('Click the button below to activate GPS testing mode, then click on the map to send fake GPS coordinates to the server.')}
                </Typography>
                
                <Button
                  variant={gpsTestingActive ? "contained" : "outlined"}
                  color={gpsTestingActive ? "secondary" : "primary"}
                  onClick={() => {
                    const newState = !gpsTestingActive;
                    setGpsTestingActive(newState);
                    onGpsTestingChange?.(newState);
                    
                    // If stopping GPS testing, send stop tracking message
                    if (gpsTestingActive && !newState && sendMessage) {
                      sendMessage({
                        type: 'stop_user_location_tracking'
                      });
                    }
                  }}
                  sx={{ mb: 2 }}
                >
                  {gpsTestingActive ? t('Stop GPS Testing') : t('Start GPS Testing')}
                </Button>
                
                {gpsTestingActive && (
                  <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="warning.contrastText">
                      ⚠️ {t('GPS Testing Mode Active')} - {t('Click anywhere on the map to send fake GPS coordinates')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {!appInfo && !rooms.length && tabValue === 0 && (
              <Typography variant="body1">
                {t('No data available')}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}